/**
 * In-memory кеш suggested questions (TTL 90 сек).
 * Архитектура: docs/AI_TUTOR_SUGGESTED_QUESTIONS_ARCHITECTURE.md
 */

export type QuestionsMeta = Record<
  string,
  {
    /** После этого вопроса можно предлагать онбординг */
    suggestOnboarding?: boolean;
    /** Какой онбординг запускать (tasklist, simulator, sandbox, lessonpage, datadocs) */
    onboardingPageKey?: string;
  }
>;

const CACHE = new Map<string, { questions: string[]; meta?: QuestionsMeta; expiresAt: number }>();
const TTL_MS = 90_000;

function djb2(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return (h >>> 0).toString(36);
}

function hashOrSlice(s: string, n: number): string {
  if (!s) return "";
  return s.length <= n ? s.replace(/[:|]/g, "_") : djb2(s.slice(0, n));
}

export interface CacheParams {
  contextType: string;
  courseSlug?: string;
  lessonId?: string;
  taskId?: string | number;
  lessonTitle?: string;
  lessonContentSnippet?: string;
  taskTitle?: string;
  lastAssistantSnippet?: string;
  /** source=ai_tutor_page — отдельный набор вопросов из конфига BE */
  source?: string;
}

export function buildCacheKey(p: CacheParams): string {
  const ct = p.contextType || "generic";
  const cs = p.courseSlug || "";
  const li = p.lessonId || "";
  const ti = p.taskId != null ? String(p.taskId) : "";
  const lt = hashOrSlice(p.lessonTitle || "", 100);
  const lc = p.lessonContentSnippet ? djb2(p.lessonContentSnippet.slice(0, 300)) : "";
  const tt = hashOrSlice(p.taskTitle || "", 80);
  const las = p.lastAssistantSnippet?.trim()
    ? djb2(p.lastAssistantSnippet.slice(0, 600))
    : "initial";
  const src = p.source || "";
  return `sq:${ct}:${cs}:${li}:${ti}:${lt}:${lc}:${tt}:${las}:${src}`;
}

export function getCached(
  key: string
): { questions: string[]; meta?: QuestionsMeta } | null {
  const entry = CACHE.get(key);
  if (!entry || Date.now() > entry.expiresAt) {
    if (entry) CACHE.delete(key);
    return null;
  }
  return { questions: entry.questions, meta: entry.meta };
}

export function setCached(
  key: string,
  questions: string[],
  meta?: QuestionsMeta
): void {
  CACHE.set(key, { questions, meta, expiresAt: Date.now() + TTL_MS });
}
